import { Link, useParams } from 'react-router-dom';
import { isOpen, MEAL_LABELS } from '@mealdesk/shared';
import CancelOrder from '../components/CancelOrder.js';
import { useT } from '../lib/i18n.js';
import { useMoney, useOrder, useTimeLeft } from '../lib/ordering.js';

/**
 * You're set — or, after a cancel, you're not.
 *
 * A green disc, the four facts worth keeping, and an amber line saying how long
 * the order stays yours. The design puts Change and Cancel side by side as
 * white cards rather than buttons competing with the confirmation — the work is
 * done, and these are afterthoughts until they are not.
 *
 * The same screen is where a cancelled order lands, from history or straight
 * after cancelling here. It used to say "You're set" for those too, with the
 * cancel button still on it.
 */
export default function ConfirmPage() {
  const { orderId } = useParams();
  const order = useOrder(orderId);
  const left = useTimeLeft(order?.editableUntil);
  const money = useMoney()(order?.price ?? 0, order?.employeeShare ?? 0, order?.currency ?? 'USD');
  const { t } = useT();

  if (!order) {
    return (
      <p className="p-6 text-[15px] text-muted md:text-[16.5px]">
        {t('That order is no longer available.')}
      </p>
    );
  }

  const cancelled = order.status === 'cancelled' || order.status === 'cancelled_exception';
  const meal = t(MEAL_LABELS[order.mealType]);

  const cutoffTime = new Date(order.editableUntil).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const serviceDate = new Date(`${order.serviceDate}T00:00:00`).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      <div className="px-6 pt-[42px] text-center">
        <div
          aria-hidden
          className={`mx-auto mb-[18px] flex size-[82px] items-center justify-center rounded-full text-[38px] ${
            cancelled ? 'bg-neutral-soft text-neutral-ink' : 'bg-brand text-white'
          }`}
        >
          {cancelled ? '✕' : '✓'}
        </div>
        <h2 className="mb-2 text-[28px] font-extrabold text-balance">
          {cancelled ? t('This order was cancelled') : t('You’re set for {meal}', { meal: meal.toLowerCase() })}
        </h2>
        <p className="text-[14.5px] text-muted md:text-[15.5px]">
          {cancelled
            ? `${meal} · ${serviceDate}`
            : order.deliveryPointName
              ? t('Pick up at {place}.', { place: order.deliveryPointName })
              : t('Your company admin will confirm the pickup point.')}
        </p>
      </div>

      <div className="px-[22px] pt-[26px]">
        <div className="rounded-panel bg-white p-5 shadow-[0_8px_26px_rgba(38,64,28,.07)]">
          <Row label={t('Order no.')} value={order.orderNo} first />
          <Row label={t('Meal')} value={`${meal} · ${serviceDate}`} />
          <Row label={t('Restaurant')} value={order.restaurantName} />
          <Row label={t('Pick up at')} value={order.deliveryPointName || t('Not set')} last={!money} />
          {money && <Row label={t('Amount')} value={money} last />}

          {!cancelled && (
            <div className="mt-1.5 rounded-chip bg-warn-soft px-[15px] py-[13px] text-[13px] leading-[1.5] text-warn-deep md:text-[14.5px]">
              {left
                ? t('Changeable for another {left} — it closes {when}. After that the restaurant is cooking.', {
                    left: left.long,
                    when: left.when,
                  })
                : t('Closed at {time}. Ask your admin if you need an exception.', { time: cutoffTime })}
            </div>
          )}
        </div>

        {!cancelled && left && (
          <div className="mt-3.5 grid gap-2.5">
            <Link
              to="/menu"
              className="flex h-[52px] items-center justify-center rounded-[17px] bg-white text-[15px] font-extrabold md:text-[16.5px]"
            >
              {t('Change')}
            </Link>
            <CancelOrder order={order} layout="block" />
          </div>
        )}

        {/* Cancelled with time still on the clock: the obvious next thing is
            to order something else for the same meal. */}
        {cancelled && isOpen(order.editableUntil) && (
          <Link
            to="/menu"
            className="mt-3.5 flex h-[52px] items-center justify-center rounded-[17px] bg-white text-[15px] font-extrabold text-brand-ink md:text-[16.5px]"
          >
            {t('Order again')}
          </Link>
        )}

        <Link
          to="/"
          className="mt-[22px] flex h-[52px] items-center justify-center rounded-[17px] bg-brand text-[15px] font-extrabold text-white md:text-[16.5px]"
        >
          {t('Back to home')}
        </Link>
      </div>
    </>
  );
}

function Row({
  label,
  value,
  first = false,
  last = false,
}: {
  label: string;
  value: string;
  first?: boolean;
  last?: boolean;
}) {
  return (
    <div
      className={`flex justify-between text-[13.5px] md:text-[15px] ${first ? 'pb-[11px]' : 'py-[11px]'} ${
        last ? '' : 'border-b border-line-soft'
      }`}
    >
      <span className="text-muted">{label}</span>
      <b className="ml-3 min-w-0 truncate text-right">{value}</b>
    </div>
  );
}
